import { HTTP_INTERCEPTORS, HttpClient } from '@angular/common/http'
import { enableProdMode, importProvidersFrom } from '@angular/core'
import { initializeApp as initFirebaseApp, provideFirebaseApp } from '@angular/fire/app'
import { AngularFireModule } from '@angular/fire/compat'
import { AngularFireMessagingModule } from '@angular/fire/compat/messaging'
import { getMessaging, provideMessaging } from '@angular/fire/messaging'
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser'
import { provideAnimations } from '@angular/platform-browser/animations'
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome'
import { setLicenseKey } from '@grapecity/wijmo'
import { QuillModule } from 'ngx-quill'
import { firstValueFrom } from 'rxjs'

import { AppRoutingModule } from './app/app-routing.module'
import { AppComponent } from './app/app.component'
import { AuthenticationService, SharedModule } from './app/shared'
import { AuthInterceptor } from './app/shared/auth-interceptor'
import { JsonDateInterceptor } from './app/shared/json-date-interceptor'
import { MeetingConnectionInterceptor } from './app/shared/meeting-connection.interceptor'
import { environment } from './environments/environment'

async function initializeApp(httpClient: HttpClient, authentication: AuthenticationService): Promise<void> {
    try {
        await firstValueFrom(authentication.completeLogin())
    } catch (error) {
        console.error('Failed to complete login initialization:', error)
    }
}

setLicenseKey(
    'webapps.wedigutah.com,818175131192545#B0IZhsnOiwmbBJye0ICRiwiI34zd4VWSXJVYmZTN8kEcL34YxNFUURTa43mWCN7aHlma5I6ZxdmQQJTZnFUQnhDazkWblhkMjVnd9Z7bjF7Qwt4Z4A5Km34aLdmbvRWYxU5QOZkTP3CUDdHNptEeZ5GV4EjUopWRmtEVoRmVz5mcppnZodjR5hVY0djdNhVdmNXVwRjQ6Z5dBhUTjJGd4QUcsF7bOJFaCNXewtSWKJUYlRWe7UGavgzc09mT9NHeq5UZaNETPJFbShXbEtCaIt6TCZnaVREaFhFaCdHONV7MRFkSJx6Rvg7TNpWdxsmV5clUxYnYv2UNsZUN5pEZrIFb4VnVp9WbsZWc6FjeBlFdpJzMxlTcZpnUIpXWZ9kZnBnYOR5SopkeoFTOLt6dkRVUVdDaxtCRZh7Q7JTRyVTZ9oHaYp6c7kGSvs6NE34QwoEbwF5KmRXYDhEU5MHd7w4L5g4dRNlettkI0IyUiwiIxUjNBVUOzMjI0ICSiwiN5gDNwkDOyQTM0IicfJye#4Xfd5nIJBjMSJiOiMkIsIibvl6cuVGd8VEIgQXZlh6U8VGbGBybtpWaXJiOi8kI1xSfiUTSOFlI0IyQiwiIu3Waz9WZ4hXRgAicldXZpZFdy3GclJFIv5mapdlI0IiTisHL3JyS7gDSiojIDJCLi86bpNnblRHeFBCI73mUpRHb55EIv5mapdlI0IiTisHL3JCNGZDRiojIDJCLi86bpNnblRHeFBCIQFETPBCIv5mapdlI0IiTisHL3JyMDBjQiojIDJCLiUmcvNEIv5mapdlI0IiTisHL3JSV8cTQiojIDJCLi86bpNnblRHeFBCI4JXYoNEbhl6YuFmbpZEIv5mapdlI0IiTis7W0ICZyBlIsIyM4czMxADI6ATOwMjMwIjI0ICdyNkIsISbvNmLoFGd5dWakV6duMHcwFmYldnI0IyctRkIsIiN7kTMiojIh94QiwiI5QTNykTMxMTM5cTM8EDOiojIklkIs4XXbpjInxmZiwiIyYnMyAjMiojIyVmdiwSZzxWYmpYIJN',
)

if (environment.production) {
    enableProdMode()
    if ('serviceWorker' in navigator) {
        navigator.serviceWorker
            .register('/firebase-messaging-sw.js')
            .then(registration => {
                console.log('Service Worker registered!', registration)
            })
            .catch(err => {
                console.error('Service Worker registration failed:', err)
            })
    }
}

// Modern initialization approach
async function bootstrapApp() {
    const providers = [
        importProvidersFrom(
            BrowserModule,
            AppRoutingModule,
            SharedModule,
            FontAwesomeModule,
            AngularFireModule.initializeApp(environment.firebase),
            AngularFireMessagingModule,
            QuillModule.forRoot(),
        ),
        {
            provide: HTTP_INTERCEPTORS,
            useClass: JsonDateInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: MeetingConnectionInterceptor,
            multi: true,
        },
        provideFirebaseApp(() =>
            initFirebaseApp({
                projectId: 'vanconwebapps',
                appId: '1:835788377075:web:a0fce7b8e9ef2e053ba086',
                storageBucket: 'vanconwebapps.firebasestorage.app',
                apiKey: 'AIzaSyBYAb3CdYBts1To7mowGMoqzwcqqZaGU2w',
                authDomain: 'vanconwebapps.firebaseapp.com',
                messagingSenderId: '835788377075',
                measurementId: 'G-C9CRVXQ1P3',
            }),
        ),
        provideMessaging(() => getMessaging()),
        provideAnimations(),
    ]

    const appRef = await bootstrapApplication(AppComponent, { providers })

    // Initialize authentication after app bootstrap
    const httpClient = appRef.injector.get(HttpClient)
    const authService = appRef.injector.get(AuthenticationService)
    await initializeApp(httpClient, authService)

    return appRef
}

bootstrapApp().catch(err => console.error(err))
